import React from 'react';
import { useEffect, useState } from 'react';
import { useConfirm } from 'material-ui-confirm';
import { Alert, Button, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, InputLabel, TextField } from '@mui/material';
import { Form, NavLink, useNavigate, useParams } from "react-router-dom";
import dayjs from 'dayjs';

import { useUserContext } from "../../../contexts/userContext";

import { IWasteCharacterization, WasteCharacterizationEmailDocumentRequest, WasteCharacterizationUpdateSignedRequest } from '../../../types/wasteCharacterization';

import { WasteCharacterizationService } from '../../../services/wasteCharacterizationService';
import { UtilityService } from '../../../services/utilityService';

import Navigation from "../../header";
import PermissionGate from "../../permissionGate";
import Spinner from '../../spinner';
import { Icons } from '../../icons';
import WasteCharacterizationDocumentViewer from '../../wasteCharacterizationDocumentViewer';

import '../../../styles/wasteCharacterizationUpdate.scss';

const WasteCharacterizationUpdate = () => {
	const confirm = useConfirm();
	const navigate = useNavigate();
	const userContext = useUserContext();
	let { id } = useParams();
	const [wasteCharacterization, setWasteCharacterization] = useState<IWasteCharacterization>();
	const [isLoadingWasteCharacterization, setIsLoadingWasteCharacterization] = useState(true);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isUploadingSigned, setIsUploadingSigned] = useState(false);
	const [isSendingEmail, setIsSendingEmail] = useState(false);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [showUploadSignedDialog, setShowUploadSignedDialog] = useState(false);
	const [showEmailDialog, setShowEmailDialog] = useState(false);
	const [profileNumber, setProfileNumber] = useState<string>('');
	const [signedDocFormFile, setSignedDocFormFile] = useState<File | null>(null);
	const [signedDocFilename, setSignedDocFilename] = useState<string>('');
	const [emailTo, setEmailTo] = useState<string>('');
	const [errorMessages, setErrorMessages] = useState<string[] | undefined>(undefined);
	const emailSubject = "HWI Environmental Waste Characterization";

	const signedDocRef = React.useRef(null);

	useEffect(() => {
		getWasteCharacterization();
	}, [id])

	useEffect(() => {
		if (wasteCharacterization) {
			setProfileNumber(wasteCharacterization.profileNumber ?? '');
		}
	}, [wasteCharacterization])

	const getWasteCharacterization = async () => {
		if (id) {
			setIsLoadingWasteCharacterization(true);
			WasteCharacterizationService.getWasteCharacterization(id)
				.then((response) => {
					console.log('WasteCharacterizationService.getWasteCharacterization: SUCCESS', response);
					setWasteCharacterization(WasteCharacterizationService.mapWasteCharacterization(response));
				})
				.catch((error) => {
					console.log('WasteCharacterizationService.getWasteCharacterization: ERROR', error)
				})
				.finally(() => {
					setIsLoadingWasteCharacterization(false);
				});
		}
	}

	const handleSignedDocChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setSignedDocFormFile(file);
			setSignedDocFilename(file.name);
		}
	}

	const handleSendEmailDocument = () => {
		setErrorMessages(undefined);
		setIsSendingEmail(true);

		const request: WasteCharacterizationEmailDocumentRequest = {
			id: wasteCharacterization?.id!,
			to: emailTo,
			subject: emailSubject
		}

		WasteCharacterizationService.emailDocument(request)
			.then((response) => {
				console.log('WasteCharacterizationService.emailDocument: SUCCESS', response);
				setWasteCharacterization({ ...wasteCharacterization!, ["emailedOn"]: new Date(), ["emailedTo"]: emailTo, ["status"]: "Emailed" });
			})
			.catch((error) => {
				console.log('WasteCharacterizationService.emailDocument: ERROR', error);

				// If the error is just that the address is invalid, tell the user
				if (error?.response?.data?.detail?.indexOf("At least one recipient is not valid") >= 0) {
					setErrorMessages([`The email address "${emailTo}" is not valid. Please check the address and try again.`]);
				}
				else {
					setErrorMessages(["An error occurred while trying to email the waste characterization document."]);
				}
			})
			.finally(() => {
				setIsSendingEmail(false);
				setShowEmailDialog(false);
			});
	}

	const confirmDocumentReplace = async () => {
		if (await
			UtilityService.ConfirmationModal(
				confirm,
				"Replace Document?",
				<>
					<p>
						This will replace the ENTIRE document, including:
						<ol>
							<li>The original HWI Waste Characterizaton Form</li>
							<li>Waste Determination documentation (Process Evaluation, SDS's)</li>
							<li>Waste Analysis documentation (TCLP Testing Information)</li>
							<li>Profile document</li>
						</ol>

						Would you like to continue?
					</p>
				</>,
				{ confirmationText: "Yes, Replace Document", confirmationButtonProps: { variant: 'contained' }, cancellationButtonProps: { color: 'error', variant: 'outlined' } }
			) == true) {
			setShowUploadSignedDialog(true);
		}		
	}

	const handleUploadSignedSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrorMessages(undefined);
		setIsUploadingSigned(true);
		const request: WasteCharacterizationUpdateSignedRequest = {
			id: wasteCharacterization?.id,
			profileNumber: profileNumber,
			document: signedDocFormFile
		}

		WasteCharacterizationService.uploadSignedDocument(request)
			.then((response) => {
				console.log('WasteCharacterizationService.uploadSignedDocument: SUCCESS', response);
				if (response?.data?.entity) {
					setWasteCharacterization(WasteCharacterizationService.mapWasteCharacterization(response.data.entity));
				}
				else {
					getWasteCharacterization();
				}
				setShowUploadSignedDialog(false);
			})
			.catch((error) => {
				console.log('WasteCharacterizationService.uploadSignedDocument: ERROR', error)
			})
			.finally(() => {
				setIsUploadingSigned(false);
			});
	}

	const handleDelete = () => {
		setErrorMessages(undefined);
		setIsDeleting(true);
		WasteCharacterizationService.deleteWasteCharacterization(id)
			.then((response) => {
				console.log('WasteCharacterizationService.deleteWasteCharacterization: SUCCESS', response);
				navigate('/wastecharacterization');
			})
			.catch((error) => {
				console.log('WasteCharacterizationService.deleteWasteCharacterization: ERROR', error);
				setErrorMessages(["An error occurred while trying to delete the waste characterization."]);
				setShowDeleteDialog(false);
				setIsDeleting(false);
			});
	}

	return (
		<>
			<div className="hwi-waste-characterization-update">
				<Navigation></Navigation>
				<Container maxWidth="lg" className="route-container">
					<PermissionGate permission={userContext.permissions?.canManageWasteCharacterization}>
						<Spinner dependency={isLoadingWasteCharacterization} text='Loading waste characterization...'>
							<section className="management-page-header">
								<div className="title">
									<NavLink to="/wastecharacterization">
										<Icons.GoBack fill="#9399ad" height="24px"></Icons.GoBack>
									</NavLink>
									Waste Characterization
								</div>
								<div className="actions">
									<Button color="primary" variant="outlined" type="button" disabled={wasteCharacterization?.status == "Completed"} onClick={() => setShowEmailDialog(true)}>Email</Button>
									<Button color="primary" variant="contained" type="button" onClick={() => confirmDocumentReplace()}>Replace Document</Button>
									<Button color="error" variant="contained" type="button" onClick={() => setShowDeleteDialog(true)}>Delete</Button>
								</div>
							</section>
							{errorMessages?.length > 0 &&
								<Alert className="alert-error"
									severity="error"
									action={<IconButton onClick={() => { setErrorMessages(undefined); }} size='small'>X</IconButton>}>
									{(errorMessages?.length ?? 0) > 0 &&
										<ul className={errorMessages.length == 1 ? 'single' : ''}>
											{errorMessages.map((msg, index) => (
												<li key={index}>{msg}</li>
											))}
										</ul>
									}
								</Alert>
							}
							{wasteCharacterization?.status == "Emailed" &&
								<>
									<Alert className="alert-info" severity="info">
										This document was emailed to <strong>{wasteCharacterization?.emailedTo ?? "(Unknown address)"}</strong> on {wasteCharacterization.emailedOn ? `${dayjs(wasteCharacterization.emailedOn).format('MMMM D, YYYY')} at ${dayjs(wasteCharacterization.emailedOn).format('h:mm A')}` : "(Unknown date)"}.
									</Alert>
									<br />
								</>
							}
							{wasteCharacterization?.status == "Completed" &&
								<>
									<Alert className="alert-info" severity="info">
										This document was signed on {wasteCharacterization.completedOn ? `${dayjs(wasteCharacterization.completedOn).format('MMMM D, YYYY')} at ${dayjs(wasteCharacterization.completedOn).format('h:mm A')}` : "(Unknown date)"}.
									</Alert>
									<br />
								</>
							}
							<div className="hwi-document-list">
								{wasteCharacterization?.mainDoc ? (
									<WasteCharacterizationDocumentViewer document={wasteCharacterization?.mainDoc}></WasteCharacterizationDocumentViewer>
								) : (
									<Alert className="alert-error" severity="error">This Waste Characterization record (ID {id}) is missing its document file.</Alert>
								)}
							</div>
						</Spinner>
						{/* <Json data={wasteCharacterization}></Json> */}
					</PermissionGate>
				</Container>
			</div>
			<Dialog
				open={showDeleteDialog}
				aria-labelledby='delete-confirmation-title'
				className='delete-confirmation-dialog'
				maxWidth='sm'
				fullWidth={true}>
				<DialogTitle id="delete-confirmation-title">
					{"Delete Document"}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						<p>Are you sure you want to delete the waste characterization of <strong>{wasteCharacterization?.materialName}</strong> for <strong>{wasteCharacterization?.customerName}</strong>?</p>
						<p>This action cannot be undone.</p>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button color="error" variant="contained" disabled={isDeleting} onClick={handleDelete}>{isDeleting ? "Deleting..." : "Delete"}</Button>
					<Button color="primary" variant='outlined' disabled={isDeleting} onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={showUploadSignedDialog}
				aria-labelledby='upload-signed-title'
				className='upload-signed-dialog'
				maxWidth='sm'
				fullWidth={true}>
				<Form onSubmit={handleUploadSignedSubmit}>
					<DialogTitle id="upload-signed-title">
						{"Upload Signed Waste Characterization"}
					</DialogTitle>
					<DialogContent>
						<DialogContentText>
							<div className="row">
								<InputLabel>Profile Number</InputLabel>
								<TextField
									type="text"
									name="profileNumber"
									required
									autoFocus={!profileNumber}
									className="profile-number-field"
									variant="outlined"
									value={profileNumber}
									size="small"
									inputProps={{ maxLength: 50 }}
									onChange={(event) => { setProfileNumber(event.target.value) }}
									onFocus={(event) => { event.target.select(); }}>
								</TextField>
							</div>
							<div className="row">
								<InputLabel>Document</InputLabel>
								<div className="custom-uploader">
									<InputLabel htmlFor="signedDocUploader" className="uploader-label">Choose</InputLabel>
									<input type="text" disabled value={signedDocFilename}></input>
								</div>
								<input type="file" id="signedDocUploader" name="signedDocUploader" ref={signedDocRef} className="uploader" accept="application/pdf" onChange={handleSignedDocChange} />
							</div>
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button color="primary" variant='contained' disabled={isUploadingSigned} type="submit">{isUploadingSigned ? "Saving..." : "Save"}</Button>
						<Button color="error" variant="contained" disabled={isUploadingSigned} onClick={() => setShowUploadSignedDialog(false)}>Cancel</Button>
					</DialogActions>
				</Form>
			</Dialog>
			<Dialog
				open={showEmailDialog}
				aria-labelledby='email-document-title'
				className='email-document-dialog'
				maxWidth='sm'
				fullWidth={true}>
				<DialogTitle id="email-document-title">
					{"Email Waste Characterization"}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						<div className="row">
							<InputLabel>To</InputLabel>
							<TextField
								type="email"
								name="emailTo"
								autoFocus
								required
								className="email-to-field"
								variant="outlined"
								value={emailTo}
								size="small"
								inputProps={{ maxLength: 50 }}
								onKeyDown={(event) => {
									if (event.key === 'Enter') {
										handleSendEmailDocument();
									}
									else if (event.key === 'Escape') {
										setShowEmailDialog(false);
									}
								}}
								onFocus={(event) => { event.target.select(); }}
								onChange={(event) => { setEmailTo(event.target.value) }}>
							</TextField>
						</div>
						<div className="row">
							<InputLabel>Subject</InputLabel>
							<TextField type="text" disabled={true} className="email-subject-field" variant="outlined" value={emailSubject} size="small"></TextField>
						</div>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button color="primary" variant='contained' disabled={isSendingEmail} onClick={handleSendEmailDocument}>{isSendingEmail ? "Sending..." : "Send"}</Button>
					<Button color="error" variant="contained" disabled={isSendingEmail} onClick={() => setShowEmailDialog(false)}>Cancel</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default WasteCharacterizationUpdate;